import { CONTEXT, getContext } from '#library/utility.library.js';
import { isNullish, isNumber, isString, isSafeKey, isObject, isReference, isPrimitive, isText, isFunction, isDefined, isCallable, isBoolean } from '#library/assertion.library.js';
import { hasOwn, hasKey } from '#library/primitive.library.js';
import { getStorage, setStorage } from '#library/storage.library.js';
import { evaluate } from '#library/evaluation.library.js';
const MAP_KEY = '_magma_geo_';
const DEFAULT_GEO_TTL = 24 * 60 * 60 * 1000; // 24 hours
const DEFAULT_REVERSE_GEO_ENDPOINT = 'https://api.bigdatacloud.net/data/reverse-geocode-client';
const DEFAULT_REVERSE_GEO_TIMEOUT = 3000;
const DEFAULT_REVERSE_GEO_MAX_BYTES = 64 * 1024;
const DEFAULT_SPATIAL_CACHE_SIZE = 1000;
/**
 * Canonical geographic property keys used for property segregation and custom key preservation.
 * @internal
 */
export const GEO_PROPERTIES = [
    'latitude',
    'lat',
    'longitude',
    'lng',
    'lon',
    'long',
    'elevation',
    'sphere',
    'country',
    'city',
    'timezone',
    'tz',
];
/**
 * Internal helper to parse a raw coordinate value (number or non-empty string) into a number.
 * Returns NaN for booleans, empty/whitespace strings, or invalid values.
 * @internal
 */
const parseCoordNumber = (val) => {
    if (!isDefined(val) || isBoolean(val))
        return NaN;
    if (isString(val)) {
        const trimmed = val.trim();
        return trimmed.length > 0 ? Number(trimmed) : NaN;
    }
    return isNumber(val) ? val : NaN;
};
/**
 * Normalizes latitude if within [-90, 90] bounds, optionally rounding to 3 decimal places.
 * @internal
 */
const normalizeLat = (lat, round = true) => {
    const n = parseCoordNumber(lat);
    if (!isNumber(n) || n < -90 || n > 90)
        return undefined;
    return round ? Math.round(n * 1000) / 1000 : n;
};
/**
 * Normalizes longitude if within [-180, 180] bounds, optionally rounding to 3 decimal places.
 * @internal
 */
const normalizeLng = (lng, round = true) => {
    const n = parseCoordNumber(lng);
    if (!isNumber(n) || n < -180 || n > 180)
        return undefined;
    return round ? Math.round(n * 1000) / 1000 : n;
};
/**
 * Validates and normalizes coordinate pairs within Earth boundaries, optionally rounding to 3 decimal places.
 * @internal
 */
const normalizeCoords = (lat, lng, round = true) => {
    const nLat = normalizeLat(lat, round);
    const nLng = normalizeLng(lng, round);
    return (isDefined(nLat) && isDefined(nLng)) ? { lat: nLat, lng: nLng } : undefined;
};
/**
 * Validates and rounds elevation in meters to 3 decimal places.
 * @internal
 */
const normalizeElevation = (elevation) => isNumber(elevation) ? Math.round(elevation * 1000) / 1000 : undefined;
/**
 * Resolves hemisphere: preserves explicit sphere if valid, or infers from latitude (+/- 0.001 band).
 * @internal
 */
const resolveSphere = (sphere, lat) => {
    if (sphere === 'north' || sphere === 'south' || sphere === 'equator')
        return sphere;
    if (isNumber(lat))
        return lat > 0.001 ? 'north' : (lat < -0.001 ? 'south' : 'equator');
    return undefined;
};
/**
 * Parses and normalizes coordinate strings ("lat, lng") or coordinate arrays ([lat, lng]).
 * @internal
 */
const parseCoordinatePair = (input, normalize = true) => {
    if (isString(input) && input.includes(',')) {
        const parts = input.split(',').map((s) => s.trim());
        return parts.length >= 2 ? normalizeCoords(parts[0], parts[1], normalize) : undefined;
    }
    return (Array.isArray(input) && input.length >= 2)
        ? normalizeCoords(input[0], input[1], normalize)
        : undefined;
};
/**
 * Assembles a clean, normalized GeoConfig object omitting empty or undefined fields.
 * @internal
 */
const assembleGeoConfig = (coords, meta) => {
    const lat = 'lat' in coords ? coords.lat : coords.latitude;
    const lng = 'lng' in coords ? coords.lng : coords.longitude;
    const nLat = normalizeLat(lat);
    const nLng = normalizeLng(lng);
    const sphere = resolveSphere(meta?.sphere, nLat ?? lat);
    const elevation = normalizeElevation(meta?.elevation);
    return {
        latitude: nLat ?? lat,
        longitude: nLng ?? lng,
        ...(sphere ? { sphere } : {}),
        ...(isText(meta?.city) ? { city: meta.city.trim() } : {}),
        ...(isText(meta?.country) ? { country: meta.country.trim() } : {}),
        ...(elevation !== undefined ? { elevation } : {}),
        ...(isText(meta?.timezone) ? { timezone: meta.timezone.trim() } : {}),
    };
};
/**
 * Safely extracts custom non-geographic properties from an object.
 * @internal
 */
export const extractCustomProperties = (source, excludedKeys = GEO_PROPERTIES) => {
    const custom = {};
    if (isReference(source)) {
        for (const key of Object.keys(source)) {
            if (isSafeKey(key) && !excludedKeys.includes(key)) {
                custom[key] = source[key];
            }
        }
    }
    return custom;
};
/**
 * Extracts and coerces latitude and longitude from input object (options, config, or instance)
 * into a canonical `{ latitude, longitude, ... }` GeoConfig object.
 * Enforces coordinate boundaries (-90 to 90 for lat, -180 to 180 for lng), 3-decimal rounding,
 * and automatic hemisphere inference ('north' | 'south' | 'equator').
 *
 * @param input - Optional object containing coordinate or geo properties
 */
export const coerceGeo = (input) => {
    const pair = parseCoordinatePair(input);
    if (pair)
        return assembleGeoConfig(pair);
    if (isPrimitive(input))
        return undefined;
    const geo = input.geo ?? input;
    const cfg = input.config?.geo ?? input.config;
    const lat = evaluate(geo?.latitude, geo?.lat, input.latitude, input.lat, cfg?.latitude, cfg?.lat);
    const lng = evaluate(geo?.longitude, geo?.lng, geo?.lon, geo?.long, input.longitude, input.lng, input.lon, input.long, cfg?.longitude, cfg?.lng, cfg?.lon, cfg?.long);
    const elevation = evaluate(geo?.elevation, input.elevation, cfg?.elevation);
    const sphere = evaluate(geo?.sphere, input.sphere, cfg?.sphere);
    const country = evaluate(geo?.country, input.country, cfg?.country);
    const city = evaluate(geo?.city, input.city, cfg?.city);
    const timezone = evaluate(geo?.timezone, geo?.tz, input.timezone, input.tz, cfg?.timezone, cfg?.tz);
    const result = {};
    const nLat = normalizeLat(lat);
    const nLng = normalizeLng(lng);
    if (nLat !== undefined)
        result.latitude = nLat;
    if (nLng !== undefined)
        result.longitude = nLng;
    const nElevation = normalizeElevation(elevation);
    if (nElevation !== undefined)
        result.elevation = nElevation;
    const resolvedSphere = resolveSphere(sphere, result.latitude);
    if (resolvedSphere)
        result.sphere = resolvedSphere;
    if (isString(country))
        result.country = country;
    if (isString(city))
        result.city = city;
    if (isString(timezone))
        result.timezone = timezone;
    const explicitGeo = isReference(input.geo) ? input.geo : (isReference(input.config?.geo) ? input.config.geo : undefined);
    const customKeys = extractCustomProperties(explicitGeo);
    Object.assign(result, customKeys);
    return Object.keys(result).length > 0 ? result : undefined;
};
/**
 * Helper to resolve storage cache keys for single-tenant default or multi-tenant scoped lookups.
 * @internal
 */
const resolveCacheKey = (keyOrOpts) => {
    if (isText(keyOrOpts)) {
        const trimmed = keyOrOpts.trim();
        return (trimmed.startsWith(MAP_KEY))
            ? trimmed
            : `${MAP_KEY}:${trimmed}`;
    }
    if (keyOrOpts && isObject(keyOrOpts)) {
        const provider = keyOrOpts.provider ?? activeGeoProvider;
        const providerPrefix = provider && isText(provider.name)
            ? `provider:${provider.name}`
            : undefined;
        const k = keyOrOpts.key ?? keyOrOpts.ip ?? keyOrOpts.query;
        if (isText(k)) {
            const trimmedKey = k.trim();
            return providerPrefix
                ? `${MAP_KEY}:${providerPrefix}:${trimmedKey}`
                : `${MAP_KEY}:${trimmedKey}`;
        }
        if (providerPrefix) {
            return `${MAP_KEY}:${providerPrefix}`;
        }
    }
    else if (activeGeoProvider && isText(activeGeoProvider.name)) {
        return `${MAP_KEY}:provider:${activeGeoProvider.name}`;
    }
    return MAP_KEY;
};
/**
 * Synchronously retrieves stashed geolocation from storage or memory cache if present.
 * Supports optional tenant key or options object for multi-tenant isolation.
 *
 * @param keyOrOpts - Optional tenant key or lookup options containing key/ip
 * @returns Cached GeoConfig or undefined
 */
export const getStashedGeo = (keyOrOpts) => {
    const cacheKey = resolveCacheKey(keyOrOpts);
    try {
        const raw = getStorage(cacheKey) ??
            (hasKey(globalThis?.localStorage, 'getItem', isCallable) ? globalThis.localStorage.getItem(cacheKey) : undefined);
        if (!raw)
            return undefined;
        const parsed = isString(raw) && (raw.startsWith('{') || raw.startsWith('['))
            ? JSON.parse(raw)
            : raw;
        if (isObject(parsed)) {
            if (isNumber(parsed._expires) && Date.now() > parsed._expires) {
                clearStashedGeo(keyOrOpts);
                return undefined;
            }
        }
        const pair = parseCoordinatePair(parsed);
        if (pair)
            return assembleGeoConfig(pair);
        if (isObject(parsed)) {
            const rawCoords = parsed.geolocation?.coords ?? parsed.coords ?? parsed;
            const lat = rawCoords?.latitude ?? rawCoords?.lat;
            const lng = rawCoords?.longitude ?? rawCoords?.lng ?? rawCoords?.lon ?? rawCoords?.long;
            const coords = normalizeCoords(lat, lng);
            if (coords) {
                const elevation = parsed.elevation ?? rawCoords.elevation;
                return assembleGeoConfig(coords, {
                    elevation,
                    sphere: parsed.sphere,
                    country: parsed.country,
                    city: parsed.city,
                    timezone: parsed.timezone,
                });
            }
        }
    }
    catch {
        // ignore storage access errors
    }
    return undefined;
};
/**
 * Explicitly stashes geolocation coordinates in storage with an optional TTL (default: 24 hours).
 * Supports optional tenant key or options object for multi-tenant isolation.
 *
 * @param coords - Geolocation coordinates and metadata to stash
 * @param ttl - Time-to-live in milliseconds (default: 24 hours)
 * @param keyOrOpts - Optional tenant key or lookup options containing key/ip
 */
export const stashGeo = (coords, ttl = DEFAULT_GEO_TTL, keyOrOpts) => {
    const cacheKey = resolveCacheKey(keyOrOpts);
    const payload = (isNumber(ttl) && ttl > 0)
        ? { ...coords, _expires: Date.now() + ttl }
        : coords;
    setStorage(cacheKey, payload, { ttl });
};
/**
 * Clears stashed geolocation coordinates from storage.
 * Supports optional tenant key or options object for multi-tenant isolation.
 *
 * @param keyOrOpts - Optional tenant key or lookup options containing key/ip
 */
export const clearStashedGeo = (keyOrOpts) => {
    const cacheKey = resolveCacheKey(keyOrOpts);
    setStorage(cacheKey, undefined);
};
let activeGeoProvider;
/**
 * Sets the active custom geolocation provider gateway.
 *
 * @param provider - GeoProvider implementation or undefined to restore default provider
 */
export function setGeoProvider(provider) {
    activeGeoProvider = provider;
}
/**
 * Gets the currently registered custom geolocation provider.
 */
export function getGeoProvider() {
    return activeGeoProvider;
}
/**
 * Universal geolocation lookup dispatcher.
 * Automatically delegates to custom provider, browser `geoLocation()`, or server `serverGeoLocation()`.
 * When coordinates are resolved, stashes the result in storage with a 24-hour TTL for fast cached lookups.
 *
 * @param opts - Lookup options passed down to environment handler (e.g. `{ refresh: true, key: 'tenant-1', provider }`)
 */
export const geoLookup = async (opts = {}) => {
    const provider = opts.provider ?? activeGeoProvider;
    const useCache = opts.refresh !== true;
    if (useCache) {
        const stashed = getStashedGeo(opts);
        if (stashed && isNumber(stashed.latitude) && isNumber(stashed.longitude)) {
            const { latitude, longitude, ...rest } = stashed;
            const cachedRes = {
                ...rest,
                status: 'cached',
                lat: latitude,
                lng: longitude,
                latitude,
                longitude,
            };
            const shouldReverse = opts.reverse === true || opts.autoReverse === true;
            if (shouldReverse && (!cachedRes.city || !cachedRes.country)) {
                try {
                    const reversed = await reverseGeocode(cachedRes, opts);
                    if (reversed) {
                        if (!cachedRes.city && isText(reversed.city))
                            cachedRes.city = reversed.city;
                        if (!cachedRes.country && isText(reversed.country))
                            cachedRes.country = reversed.country;
                    }
                }
                catch (err) {
                    cachedRes.reverseError = err?.message ?? String(err);
                }
            }
            return cachedRes;
        }
    }
    let res;
    if (provider && isFunction(provider.lookup)) {
        try {
            const providerRes = await provider.lookup(opts);
            if (providerRes) {
                res = providerRes;
                if (providerRes.error && opts.fallback === false)
                    return providerRes;
            }
            else if (opts.fallback === false) {
                return { error: 'Provider returned null and fallback is disabled' };
            }
        }
        catch (err) {
            if (opts.fallback === false)
                return { error: err?.message ?? 'Provider lookup failed' };
        }
    }
    if (!res || res.error) {
        if (opts.fallback === false && (provider || res?.error))
            return res && res.error ? res : { error: 'Geolocation resolution failed and fallback is disabled' };
        const { type } = getContext();
        switch (type) {
            case CONTEXT.Browser: {
                const { geoLocation } = await import('#browser/mapper.library.js');
                const browserRes = await geoLocation(opts);
                if (browserRes.error)
                    return { error: browserRes.error };
                const lat = browserRes.coords?.latitude;
                const lng = browserRes.coords?.longitude;
                res = { ...browserRes, lat, lng, latitude: lat, longitude: lng };
                break;
            }
            case CONTEXT.WebWorker:
            case CONTEXT.NodeJS:
            case CONTEXT.Deno:
            default: {
                const { serverGeoLocation } = await import('#server/mapper.library.js');
                res = await serverGeoLocation(opts);
                break;
            }
        }
    }
    // Stash successfully resolved coordinates with 24-hour TTL
    const rawLat = res.latitude ?? res.lat;
    const rawLng = res.longitude ?? res.lng;
    const coords = normalizeCoords(rawLat, rawLng);
    if (isNullish(res.error) && coords) {
        const sphere = resolveSphere(res.sphere, coords.lat);
        res.lat = coords.lat;
        res.lng = coords.lng;
        res.latitude = coords.lat;
        res.longitude = coords.lng;
        res.sphere = sphere;
        const shouldReverse = opts.reverse === true || opts.autoReverse === true;
        if (shouldReverse && (!res.city || !res.country)) {
            try {
                const reversed = await reverseGeocode(res, opts);
                if (reversed) {
                    if (!res.city && isText(reversed.city))
                        res.city = reversed.city;
                    if (!res.country && isText(reversed.country))
                        res.country = reversed.country;
                }
            }
            catch (err) {
                res.reverseError = err?.message ?? String(err);
            }
        }
        const stashPayload = assembleGeoConfig(coords, res);
        try {
            const ttl = isNumber(opts.ttl) ? opts.ttl : DEFAULT_GEO_TTL;
            stashGeo(stashPayload, ttl, opts);
        }
        catch {
            // ignore storage errors
        }
    }
    return res;
};
import { BoundedCache } from '#library/cache.class.js';
let requestModulePromise = null;
const getRequestModule = () => {
    if (!requestModulePromise)
        requestModulePromise = import('./request.library.js');
    return requestModulePromise;
};
// Dedicated spatial reverse cache (max 1,000 locations, 24h TTL)
const reverseGeoCache = new BoundedCache(DEFAULT_SPATIAL_CACHE_SIZE, DEFAULT_GEO_TTL);
/**
 * Validates custom reverse geocoding endpoints: requires HTTPS or local development origin.
 * Endpoints must only be configured with trusted administrative origins and not untrusted input.
 * @internal
 */
const isValidGeoEndpoint = (urlStr) => {
    try {
        const parsed = new URL(urlStr);
        return parsed.protocol === 'https:' || parsed.hostname === 'localhost' || parsed.hostname === '127.0.0.1';
    }
    catch {
        return false;
    }
};
/**
 * Returns the active BoundedCache instance used for spatial reverse geocoding deduplication.
 */
export const getSpatialReverseCache = () => reverseGeoCache;
/**
 * Computes a 2-decimal spatial bucket key (~1.1 km resolution) for coordinate caching,
 * partitioned by lookup source, provider identity, or custom endpoint.
 */
export const getSpatialCacheKey = (lat, lng, sourceOrOpts) => {
    const bucket = `${lat.toFixed(2)},${lng.toFixed(2)}`;
    if (isText(sourceOrOpts))
        return `${bucket}:${sourceOrOpts.trim()}`;
    if (isObject(sourceOrOpts)) {
        const hasExplicitProvider = hasOwn(sourceOrOpts, 'provider');
        const provider = hasExplicitProvider ? sourceOrOpts.provider : activeGeoProvider;
        const providerName = provider && isText(provider.name) ? provider.name.trim() : undefined;
        const rawEndpoint = isText(sourceOrOpts.reverseEndpoint ?? sourceOrOpts.reverseGeoEndpoint)
            ? (sourceOrOpts.reverseEndpoint ?? sourceOrOpts.reverseGeoEndpoint).trim()
            : undefined;
        const endpoint = rawEndpoint && isValidGeoEndpoint(rawEndpoint) ? rawEndpoint : undefined;
        if (providerName && endpoint)
            return `${bucket}:${providerName}:${endpoint}`;
        if (providerName)
            return `${bucket}:${providerName}`;
        if (endpoint)
            return `${bucket}:${endpoint}`;
    }
    else if (activeGeoProvider && isText(activeGeoProvider.name)) {
        return `${bucket}:${activeGeoProvider.name.trim()}`;
    }
    return bucket;
};
/**
 * Reverse geocodes coordinates to address or location metadata using the active or provided GeoProvider.
 * Employs 2-decimal spatial deduplication with BoundedCache (24h TTL).
 *
 * @param coords - Coordinate input
 * @param opts - Options including optional provider override and { refresh: true }
 */
export async function reverseGeocode(coords, opts) {
    const coerced = coerceGeo(coords);
    if (!coerced || !isNumber(coerced.latitude) || !isNumber(coerced.longitude))
        return null;
    const lat = coerced.latitude;
    const lng = coerced.longitude;
    const key = getSpatialCacheKey(lat, lng, opts);
    const useCache = opts?.refresh !== true;
    if (useCache) {
        const cached = reverseGeoCache.get(key);
        if (cached) {
            return {
                ...cached,
                latitude: lat,
                longitude: lng,
                sphere: resolveSphere(cached.sphere, lat),
                elevation: isNumber(cached.elevation) ? cached.elevation : coerced.elevation,
                timezone: isString(cached.timezone) ? cached.timezone : coerced.timezone,
            };
        }
    }
    const hasExplicitProvider = hasOwn(opts, 'provider');
    const provider = hasExplicitProvider ? opts?.provider : activeGeoProvider;
    if (provider && isFunction(provider.reverseGeocode)) {
        try {
            const res = await provider.reverseGeocode(coerced, opts);
            if (res && isObject(res)) {
                const result = assembleGeoConfig({ lat, lng }, {
                    sphere: res.sphere,
                    city: res.city,
                    country: res.country,
                    elevation: isNumber(res.elevation) ? res.elevation : coerced.elevation,
                    timezone: isString(res.timezone) ? res.timezone : coerced.timezone,
                });
                const cachePayload = assembleGeoConfig({ lat, lng }, {
                    sphere: res.sphere,
                    city: res.city,
                    country: res.country,
                    timezone: res.timezone,
                });
                reverseGeoCache.set(key, cachePayload);
                return result;
            }
            if (opts?.fallback === false)
                return null;
        }
        catch (err) {
            if (opts?.fallback === false)
                throw err;
        }
    }
    else if (opts?.fallback === false && (provider || hasExplicitProvider)) {
        return null;
    }
    // Default lightweight reverse geocoding via BigDataCloud client API or custom reverseEndpoint
    const rawReverseEndpoint = opts?.reverseEndpoint ?? opts?.reverseGeoEndpoint;
    if (isText(rawReverseEndpoint)) {
        const rawEndpoint = rawReverseEndpoint.trim();
        if (!isValidGeoEndpoint(rawEndpoint))
            return opts?.fallback === false ? null : coerced;
    }
    const customEndpoint = isText(rawReverseEndpoint) ? rawReverseEndpoint.trim() : undefined;
    const fallbackKey = getSpatialCacheKey(lat, lng, customEndpoint ?? 'default');
    if (useCache) {
        const cachedFallback = reverseGeoCache.get(fallbackKey);
        if (cachedFallback) {
            return {
                ...cachedFallback,
                latitude: lat,
                longitude: lng,
                sphere: resolveSphere(cachedFallback.sphere, lat),
                elevation: isNumber(cachedFallback.elevation) ? cachedFallback.elevation : coerced.elevation,
                timezone: isString(cachedFallback.timezone) ? cachedFallback.timezone : coerced.timezone,
            };
        }
    }
    let endpoint;
    if (customEndpoint) {
        if (/\{(?:latitude|lat|longitude|lng|lon)\}/i.test(customEndpoint)) {
            endpoint = customEndpoint
                .replace(/\{latitude\}/gi, encodeURIComponent(lat))
                .replace(/\{lat\}/gi, encodeURIComponent(lat))
                .replace(/\{longitude\}/gi, encodeURIComponent(lng))
                .replace(/\{lng\}/gi, encodeURIComponent(lng))
                .replace(/\{lon\}/gi, encodeURIComponent(lng));
        }
        else {
            const sep = customEndpoint.includes('?') ? '&' : '?';
            endpoint = `${customEndpoint}${sep}latitude=${encodeURIComponent(lat)}&longitude=${encodeURIComponent(lng)}`;
        }
    }
    else {
        endpoint = `${DEFAULT_REVERSE_GEO_ENDPOINT}?latitude=${lat}&longitude=${lng}&localityLanguage=en`;
    }
    try {
        const timeout = isNumber(opts?.timeout) && opts.timeout > 0 ? opts.timeout : DEFAULT_REVERSE_GEO_TIMEOUT;
        const maxBytes = isNumber(opts?.maxBytes) && opts.maxBytes > 0 ? opts.maxBytes : DEFAULT_REVERSE_GEO_MAX_BYTES;
        const { fetchRequest } = await getRequestModule();
        const data = await fetchRequest(endpoint, {}, { timeout, maxBytes });
        if (isObject(data)) {
            const city = isText(data.city)
                ? data.city.trim()
                : (isText(data.locality) ? data.locality.trim() : (isText(data.principalSubdivision) ? data.principalSubdivision.trim() : undefined));
            const country = isText(data.countryCode)
                ? data.countryCode.trim()
                : (isText(data.countryName) ? data.countryName.trim() : undefined);
            const result = assembleGeoConfig({ lat, lng }, {
                city,
                country,
                elevation: coerced.elevation,
                timezone: coerced.timezone,
            });
            if (isText(city) || isText(country)) {
                const cachePayload = assembleGeoConfig({ lat, lng }, {
                    city,
                    country,
                });
                reverseGeoCache.set(fallbackKey, cachePayload);
            }
            return result;
        }
    }
    catch {
        // Graceful fallback to raw coerced coordinates on network/offline error
    }
    return coerced;
}
/**
 * Forward geocodes an address or place query string into coordinates using the active or provided GeoProvider.
 *
 * @param query - Address or place search query
 * @param opts - Options including optional provider override
 */
export async function forwardGeocode(query, opts) {
    const provider = opts?.provider ?? activeGeoProvider;
    return (provider && isFunction(provider.forwardGeocode))
        ? provider.forwardGeocode(query, opts)
        : null;
}
/**
 * Universal coordinate resolver.
 * Extracts latitude and longitude from input object (instance, config, options),
 * or triggers `geoLookup()` if coordinates are omitted.
 * Supports opt-in reverse lookup (`{ reverse: true }`).
 * Enforces 3-decimal rounding and returns rich metadata when resolved.
 *
 * @param input - Optional object containing coordinate properties
 * @param opts - Fallback geoLookup options if coordinates are missing from input
 */
export const resolveGeoCoordinates = async (input, opts = {}) => {
    const effectiveOpts = isObject(input)
        ? { ...input, ...opts }
        : opts;
    const coerced = coerceGeo(input);
    if (coerced && isNumber(coerced.latitude) && isNumber(coerced.longitude)) {
        const shouldReverse = effectiveOpts.reverse === true || effectiveOpts.autoReverse === true;
        if (shouldReverse && (!coerced.city || !coerced.country)) {
            const reversed = await reverseGeocode(coerced, effectiveOpts);
            if (reversed) {
                return {
                    ...reversed,
                    ...coerced,
                    city: isText(coerced.city) ? coerced.city : reversed.city,
                    country: isText(coerced.country) ? coerced.country : reversed.country,
                    lat: coerced.latitude,
                    lng: coerced.longitude,
                    latitude: coerced.latitude,
                    longitude: coerced.longitude,
                };
            }
        }
        return {
            ...coerced,
            lat: coerced.latitude,
            lng: coerced.longitude,
        };
    }
    const stashed = effectiveOpts.refresh === true ? undefined : getStashedGeo(effectiveOpts);
    if (stashed && isNumber(stashed.latitude) && isNumber(stashed.longitude)) {
        const shouldReverse = effectiveOpts.reverse === true || effectiveOpts.autoReverse === true;
        if (shouldReverse && (!stashed.city || !stashed.country)) {
            const reversed = await reverseGeocode(stashed, effectiveOpts);
            if (reversed) {
                return {
                    ...reversed,
                    ...stashed,
                    city: isText(stashed.city) ? stashed.city : reversed.city,
                    country: isText(stashed.country) ? stashed.country : reversed.country,
                    lat: stashed.latitude,
                    lng: stashed.longitude,
                    latitude: stashed.latitude,
                    longitude: stashed.longitude,
                };
            }
        }
        return {
            ...stashed,
            lat: stashed.latitude,
            lng: stashed.longitude,
        };
    }
    const lookup = await geoLookup(effectiveOpts);
    const coords = normalizeCoords(lookup.lat, lookup.lng);
    if (isNullish(lookup.error) && coords) {
        const sphere = resolveSphere(lookup.sphere, coords.lat);
        const base = {
            ...assembleGeoConfig(coords, { ...lookup, sphere }),
            lat: coords.lat,
            lng: coords.lng,
            latitude: coords.lat,
            longitude: coords.lng,
            sphere,
        };
        const shouldReverse = effectiveOpts.reverse === true || effectiveOpts.autoReverse === true;
        if (shouldReverse && (!base.city || !base.country)) {
            const reversed = await reverseGeocode(base, effectiveOpts);
            if (reversed) {
                return {
                    ...reversed,
                    ...base,
                    city: isText(base.city) ? base.city : reversed.city,
                    country: isText(base.country) ? base.country : reversed.country,
                    lat: coords.lat,
                    lng: coords.lng,
                    latitude: coords.lat,
                    longitude: coords.lng,
                    sphere,
                };
            }
        }
        return base;
    }
    return null;
};
